import React from 'react';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { generateGradientsAndColors, formatLabel, NivoTextTheme } from "./utils";

/**
 * NivoHeatMap Component
 * @param data {Array} - Heatmap data
 * @param keys {Array} - Heatmap keys for x-axis
 * @param precision {Number} - Number of decimal places for cell labels
 * @param postfix {String} - Postfix for cell labels
 * @param minValue {Number} - Minimum value for color range
 * @param maxValue {Number} - Maximum value for color range
 * @param labelSize {Number} - Font size for labels
 * @param labelColor {String} - Color for labels
 * @param showUpperAxis {Boolean} - Whether to show the upper x-axis
 * @param padLeft {Number} - Padding for the left margin
 * @returns {JSX.Element}
 * @constructor
 */
const NivoHeatMap = ({
  data,
  keys,
  precision = 0,
  postfix = '%',
  minValue = -100,
  maxValue = 100,
  labelSize = 12,
  labelColor = '#fff',
  showUpperAxis = true,
    axisTitle='',
    axisLeft=false,
    axisTop=true,
    axisTitleSize=20,
  padLeft = 30,
}) => {
  const { gradients, colorMapping } = generateGradientsAndColors(keys);
  const labelIncr = labelSize < 10 ? 2 : 0;

  return (
    <ResponsiveHeatMap
      data={data}
      keys={keys}
      indexBy="id"
      margin={{ top: 25, right: 5, bottom: 5, left: padLeft }}
      forceSquare={true}
      axisRight={null}
      axisBottom={null}
      opacity={0.9}
      axisLeft={{
        orient: 'left',
        legend: axisLeft ? axisTitle : '',
        legendPosition: 'middle',
        legendOffset: -90,
          fontSize: axisTitleSize,
      }}
      axisTop={{
          orient: 'top',
            legend: axisTop ? axisTitle : '',
            legendPosition: 'middle',
            fontSize: axisTitleSize,
      }}
      cellBorderColor={{ from: 'color', modifiers: [['brighter', 0.4]] }}
      labelTextColor={{
        from: 'color',
        modifiers: [['brighter', 2]],
      }}
      label={(d) => formatLabel(d, precision, postfix)}
      cellShape={(props) => (
        <rect
          {...props}
          style={{
            ...props.style,
            fill: `url(#gradient-${props.key})`,
          }}
        />
      )}
      defs={keys.map((key) => (
        <linearGradient
          key={key}
          id={`gradient-${key}`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="0%" stopColor={colorMapping[key]} stopOpacity={0.3} />
          <stop offset="100%" stopColor={colorMapping[key]} stopOpacity={1} />
        </linearGradient>
      ))}
      colors={{ type: 'diverging', scheme: 'blues', divergeAt: 0.5, minValue, maxValue }}
      legends={[
        {
          anchor: 'bottom',
          translateX: 0,
          translateY: 30,
          length: 150,
          itemHeight: 20,
          symbolSize: 10,
          direction: 'row',
          thickness: 12,
          tickPosition: 'after',
          tickSize: 0,
          tickSpacing: 4,
          tickFormat: '>-.0s',
          title: 'Value →',
          titleAlign: 'start',
          titleOffset: 4,
        },
      ]}
      animate={true}
      motionConfig="gentle"
      theme={{
        ...NivoTextTheme,
        labels: { text: { fontSize: labelSize, fontWeight: '700' } },
        axis: { ticks: { text: { fontSize: labelSize + labelIncr, fontWeight: '500' } } },
      }}
    />
  );
};

export default NivoHeatMap;

/**
 * Transform Data for Nivo HeatMap
 * @param flatData {Array} - Flattened data in the form of [{ x, y, value }]
 * @returns {Array} - Transformed data suitable for the NivoHeatMap component
 */
export const transformDataForNivoHeatMap = (flatData) => {
  // Get unique keys from both x and y dimensions
  const uniqueKeys = [...new Set([
    ...flatData.map((item) => item.x),
    ...flatData.map((item) => item.y)
  ])].sort();

  // Create a lookup map for quick value access
  const valueLookup = new Map(
    flatData.map(item => [`${item.x}-${item.y}`, item.value])
  );

  // Transform data into Nivo format with full matrix
  const transformedData = uniqueKeys.map((keyY) => {
    const row = {
      id: keyY.toUpperCase(),
      data: uniqueKeys.map((keyX) => {
        // Try both combinations to handle symmetric matrices
        const value = valueLookup.get(`${keyX}-${keyY}`) ??
                     valueLookup.get(`${keyY}-${keyX}`) ??
                     0;

        return {
          x: keyX.toUpperCase(),
          y: value
        };
      })
    };
    return row;
  });

  return transformedData;
};
